import React, { useEffect, useState, ChangeEvent, useRef } from "react";
import "./invoice.css";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface LineItem {
  id: number;
  description: string;
  price: number;
}

interface InvoiceForm {
  invoice_date: string;
  invoice_due_date: string;
  service_type: string;
  payment_status: string;
  company_name: string;
  email: string;
  phone: string;
  country: string;
  billing: string;
  status: "pending" | "paid" | "draft";
  notes: string;
  customer_notes: string;
  payment_terms: string;
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API_GET_TENANT = `${BASE_URL}/tenants`;
const API_CREATE = `${BASE_URL}/tenant/invoice/add`;
const API_GET_INVOICE_BY_TENANT =
  `${BASE_URL}/tenant/invoice/tenant`;

const INVOICE_PREFIX = "INV-2026-";

const CreateInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [suffix, setSuffix] = useState<string>("001");
  const [loading, setLoading] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const [form, setForm] = useState<InvoiceForm>({
    invoice_date: "",
    invoice_due_date: "",
    service_type: "",
    payment_status: "pending",
    company_name: "",
    email: "",
    phone: "",
    country: "UAE",
    billing: "",
    status: "pending",
    notes: "",
    customer_notes: "",
    payment_terms: "Net 14 days",
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [grandTotal, setGrandTotal] = useState<number>(0);

  const invoiceNumber = `${INVOICE_PREFIX}${suffix}`;

  // PDF capture ref
  const pdfRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        
        const resTenant = await axios.get(`${API_GET_TENANT}/${id}`);
        const t = resTenant.data.data;

        setForm((prev) => ({
          ...prev,
          company_name: t.agency_name || "N/A",
          email: t.admin_email || "N/A",
          status: t.status?.toLowerCase() || "pending",
        }));

        const item: LineItem = {
          id: Date.now(),
          description: t.plan_name || "Subscription Plan",
          price: Number(t.mrr) || 0,
        };

        setLineItems([item]);
        setGrandTotal(item.price);

        //  Existing invoice (agar hai to)
        try {
          const resInvoice = await axios.get(
            `${API_GET_INVOICE_BY_TENANT}/${id}`,
          );
          const inv = resInvoice.data.data;

          setForm((prev) => ({
            ...prev,
            invoice_date: inv.invoice_date ? inv.invoice_date.slice(0, 10) : "",
            invoice_due_date: inv.due_date ? inv.due_date.slice(0, 10) : "",
            service_type: inv.service_type || "",
            payment_status: inv.payment_status || "pending",
            phone: inv.phone || "",
            country: inv.country || "UAE",
            billing: inv.billing_address || "",
            status: inv.status || prev.status,
          }));

          if (
            inv.invoice_number &&
            inv.invoice_number.startsWith(INVOICE_PREFIX)
          ) {
            setSuffix(inv.invoice_number.replace(INVOICE_PREFIX, ""));
          }

          if (inv.total_amount) {
            setGrandTotal(Number(inv.total_amount));
          }
        } catch {
          // invoice nahi mila, ignore
        }
      } catch (err) {
        toast.error("Failed to fetch tenant data");
      }
    };

    fetchData();
  }, [id]);

  const handleSuffixChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    setSuffix(value);
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    if (!form.invoice_date || !form.invoice_due_date || !form.service_type) {
      toast.error("Invoice date, due date & service type required");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        tenant_id: id,
        invoice_number: invoiceNumber,
        invoice_date: form.invoice_date,
        invoice_due_date: form.invoice_due_date,
        service_type: form.service_type,
        payment_status: form.payment_status,
        total_amount: grandTotal,
        status: form.status,
        phone: form.phone,
        country: form.country,
        billing_address: form.billing,
      };

      const res = await axios.post(API_CREATE, payload);

      if (res.data.success) {
        toast.success("Invoice Saved Successfully!");
        navigate("/dashboard/tenants");
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || "API Error");
      } else if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPreview = () => {
    if (!form.invoice_date || !form.invoice_due_date || !form.service_type) {
      toast.error("Invoice date, due date & service type required");
      return;
    }
    setShowPreview(true);
  };

  const handleDownloadPDF = async () => {
    if (!pdfRef.current) return;

    try {
      const element = pdfRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${invoiceNumber}.pdf`);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to generate PDF");
    }
  };

  const formatDate = (iso: string) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
   <div className="container-invoice">
     <div className="view" id="view-create">
      {/* HEADER */}
      <div className="page-head">
        <div>
          <h1>Create Invoice</h1>
          <p>Quick manual entry with description and price</p>
        </div>
        <div className="page-head-actions">
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleOpenPreview}
          >
            PDF
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save & Issue"}
          </button>
        </div>
      </div>

      <div className="layout-grid">
        <div className="layout-left">
          {/* Invoice Details */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Invoice Details</div>
            </div>
            <div className="card-body">
              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label className="form-label">Invoice Number</label>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <input
                      className="form-control readonly-input"
                      value={INVOICE_PREFIX}
                      readOnly
                    />
                    <input
                      className="form-control"
                      value={suffix}
                      onChange={handleSuffixChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Invoice Date</label>
                  <input
                    className="form-control"
                    type="date"
                    id="invoice_date"
                    value={form.invoice_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input
                    className="form-control"
                    type="date"
                    id="invoice_due_date"
                    value={form.invoice_due_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Service Type <span className="req">*</span>
                  </label>
                  <select
                    className="form-control"
                    id="service_type"
                    value={form.service_type}
                    onChange={handleChange}
                  >
                    <option value="">Select service…</option>
                    <option value="flight">✈ Flight</option>
                    <option value="hotel">🏨 Hotel</option>
                    <option value="visa">🛂 Visa</option>
                    <option value="hajj">🕌 Hajj / Murat</option>
                    <option value="tour">🗺 Tour Package</option>
                    <option value="car">🚗 Car Rental</option>
                    <option value="custom">📝 Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Status</label>
                  <select
                    className="form-control"
                    id="payment_status"
                    value={form.payment_status}
                    onChange={handleChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Customer */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Customer / Client</div>
            </div>
            <div className="card-body">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Client Name</label>
                  <input
                    className="form-control"
                    value={form.company_name}
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-control" value={form.email} readOnly />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    className="form-control"
                    id="phone"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Country</label>
                  <select
                    className="form-control"
                    id="country"
                    value={form.country}
                    onChange={handleChange}
                  >
                    <option>UAE</option>
                    <option>India</option>
                    <option>Saudi Arabia</option>
                    <option>Qatar</option>
                    <option>UK</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Billing Address</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    id="billing"
                    value={form.billing}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Plans</div>
            </div>
            <div className="card-body">
              {lineItems.map((item) => (
                <div className="line-item" key={item.id}>
                  <input
                    className="form-control"
                    value={item.description}
                    readOnly
                  />
                  <input className="form-control" value={item.price} readOnly />
                </div>
              ))}
            </div>

            <div className="card-footer">
              <div style={{ textAlign: "right" }}>
                <strong>Total: ${grandTotal} Mrr</strong>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="layout-right">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Summary</div>
            </div>
            <div className="card-body">
              <div style={{ display:"flex", justifyContent:"space-between"}}>
                <div style={{color:"gray" , fontSize:"12px"}}>Invoice #:</div>
                <div style={{color:"blue" , fontWeight:"bold", fontSize:"14px"}}>{invoiceNumber}</div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between" , margin:"10px 0px"}}>
                <div style={{color:"gray" , fontSize:"12px"}}>Company Name #:</div>
                <div style={{color:"black" , fontWeight:"bold", fontSize:"14px"}}>{form.company_name}</div>
              </div>
              
               <div style={{ display:"flex", justifyContent:"space-between"}}>
                <div style={{color:"gray" , fontSize:"12px"}}>Status #:</div>
                <div style={{color:"black" , fontWeight:"bold", fontSize:"14px"}}>{form.status}</div>
              </div>
               
              <div style={{borderTop:"1px solid #e0e0e0", margin:"12px auto"}}></div>

              <div style={{ display:"flex", justifyContent:"space-between" , margin:"10px 0px"}}>
                <div style={{color:"gray" , fontSize:"12px"}}>Plan Amount #:</div>
                <div style={{color:"blue" , fontWeight:"bold", fontSize:"14px"}}>$ {grandTotal}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PREVIEW MODAL */}
      {showPreview && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Invoice Preview</h2>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={handleDownloadPDF}
                >
                  Download PDF
                </button>
                <button
                  className="btn btn-ghost"
                  type="button"
                  onClick={() => setShowPreview(false)}
                >
                  Close
                </button>
              </div>
            </div>

            <div ref={pdfRef} className="invoice-preview">
              {/* Header */}
              <div className="inv-header">
                <div className="inv-logo">
                  <div className="inv-logo-mark">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5" />
                      <path d="M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <div>
                    <div className="inv-logo-name">FlyGDS</div>
                    <div className="inv-logo-tagline">
                      Travel Technology Platform
                    </div>
                  </div>
                </div>
                <div className="inv-meta">
                  <div className="inv-word">Invoice</div>
                  <div className="inv-number">#{invoiceNumber}</div>
                  <div className="inv-date-row">
                    <span className="inv-date-label">Issued:</span>{" "}
                    {formatDate(form.invoice_date)}
                  </div>
                  <div className="inv-date-row">
                    <span className="inv-date-label">Due:</span>{" "}
                    {formatDate(form.invoice_due_date)}
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="inv-body">
                {/* Parties */}
                <div className="inv-parties">
                  <div>
                    <div className="inv-party-label">From</div>
                    <div className="inv-party-name">FlyGDS Technologies</div>
                    <div className="inv-party-detail">
                      admin@flygds.com
                      <br />
                      +91 98765 43210
                      <br />
                      Aloha, Khatushyam Ji, Rajasthan
                      <br />
                      GST IN: 08AAAA1234A1Z5
                    </div>
                  </div>
                  <div>
                    <div className="inv-party-label">Billed To</div>
                    <div className="inv-party-name">{form.company_name}</div>
                    <div className="inv-party-detail">
                      {form.email}
                      <br />
                      {form.phone}
                      <br />
                      {form.billing}
                    </div>
                  </div>
                </div>

                {/* Items */}
                <table className="inv-items-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Description</th>
                      <th style={{ width: 110, textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, idx) => (
                      <tr key={item.id}>
                        <td>{(idx + 1).toString().padStart(2, "0")}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {item.description}
                          </div>
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            fontFamily: "var(--mono)",
                            fontWeight: 700,
                          }}
                        >
                          {item.price.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    {lineItems.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          style={{
                            textAlign: "center",
                            color: "var(--ink3)",
                          }}
                        >
                          No line items
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan={2} style={{ textAlign: "right" }}>
                        Total Amount Due
                      </td>
                      <td>{grandTotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>

                {/* Footer */}
                <div className="inv-footer">
                  <div>
                    <div className="inv-notes-label">Notes</div>
                    <div
                      className="inv-notes-text"
                      style={{ color: "gray", marginTop: 4 }}
                    >
                      Thank you for choosing FlyGDS. This invoice is
                      computer-generated and does not require a physical
                      signature.
                    </div>
                  </div>
                  <div className="inv-status-area">
                    <div className="inv-status-label">Payment Status</div>
                    <div className="inv-status-badge">
                      {form.payment_status.toUpperCase()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /invoice-preview */}
          </div>
        </div>
      )}
    </div>
   </div>
  );
};

export default CreateInvoicePage;
